import React, { useCallback, useMemo, useState } from "react";

import {
  DiscountTierDto,
  ExperienceCurrencyDto,
  OfferDto,
  OfferQuantityButtonType,
  QuantityButtonProductVariant,
  QuantityButtonsConfig,
  QuantityButtonsWidgetDto,
  WidgetDeviceType,
} from "@intelligems/ig-types";
import { ensureNumber } from "@intelligems/ig-utils";
import { QuantityButtonsView } from "@intelligems/ig-widgets";
import { Grid, SxProps } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";

interface IgDiscountQuantityWidgetProps {
  device: WidgetDeviceType;
  widget: QuantityButtonsWidgetDto;
  offer: OfferDto;
  focusedOfferVariant?: QuantityButtonProductVariant | null;
  gridSx?: SxProps;
}

const getDiscountedPrice = (
  basePrice: number,
  offer: OfferDto,
  buttonQuantity: number,
  tier: DiscountTierDto | undefined
) => {
  const ensuredUnitDiscount = tier?.unitDiscount ?? 0;
  if (offer.discountType === "dollar") {
    if (offer.discountApplicationMethod === "perItem") {
      return (basePrice - ensuredUnitDiscount) * buttonQuantity;
    } else {
      return (
        (basePrice * buttonQuantity - ensuredUnitDiscount) / buttonQuantity
      );
    }
  } else {
    const calculatedDollarDiscount =
      Math.floor(
        (basePrice - basePrice * (1 - ensuredUnitDiscount / 100)) * 100
      ) / 100;

    return (basePrice - calculatedDollarDiscount) * buttonQuantity;
  }
};

export const IgQuantityButtons = ({
  device,
  widget,
  offer,
  focusedOfferVariant,
  gridSx,
}: IgDiscountQuantityWidgetProps) => {
  const currencyFormat = useConfiguration("currency_format")?.value;
  const buttons = widget.config[device]?.variables?.buttons ?? [];
  const [quantity, setQuantity] = useState(buttons[0]?.quantity ?? 0);
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat("en", {
        style: "currency",
        ...currencyFormat.options,
        currency: currencyFormat?.code || "USD",
      }),
    [currencyFormat]
  );
  const config = useMemo(
    (): QuantityButtonsConfig => ({
      widget: widget.config,
      discountType: offer.discountType,
    }),
    [widget.config, offer.discountType]
  );

  const numberWrapper = (formattedPrice: string) => {
    if (currencyFormat.removeTrailingZeros) {
      return formattedPrice.replace(/\D00(?=\D*$)/, "");
    } else return formattedPrice;
  };

  const calculateButtonState = useCallback(
    async (button: OfferQuantityButtonType) => {
      const tier = getTierForQuantity(offer, button.quantity);

      const ogBasePrice = focusedOfferVariant
        ? ensureNumber(focusedOfferVariant.price)
        : 100;

      const unitDiscount = tier?.unitDiscount ?? 0;
      const price = getDiscountedPrice(
        ogBasePrice,
        offer,
        button.quantity,
        tier
      );

      const perUnitNewPrice = getDiscountedPrice(
        ogBasePrice,
        offer,
        offer.discountApplicationMethod === "perOrder" ? button.quantity : 1,
        tier
      );
      const discountedUnitPrice = ogBasePrice - unitDiscount;

      const state = {
        unitDiscount,
        discountedUnitPrice: numberWrapper(
          formatter.format(discountedUnitPrice)
        ),
        product: {
          basePrice: ogBasePrice,
          formattedBasePrice: numberWrapper(formatter.format(ogBasePrice)),
          productId: focusedOfferVariant
            ? focusedOfferVariant.productId
            : "123",
          variantId: focusedOfferVariant ? focusedOfferVariant.id : "345",
          productTitle: focusedOfferVariant
            ? focusedOfferVariant.productTitle
            : "Fake product title",
        },
        productTitle: focusedOfferVariant
          ? focusedOfferVariant.productTitle
          : "Fake product title",
        originalTotalPrice: numberWrapper(
          formatter.format(ogBasePrice * button.quantity)
        ),
        originalPricePerItem: numberWrapper(formatter.format(ogBasePrice)),
        newTotalPrice: numberWrapper(formatter.format(price)),
        newPricePerItem: numberWrapper(formatter.format(perUnitNewPrice)),
        savedTotalAmount: numberWrapper(
          formatter.format(ogBasePrice * button.quantity - price)
        ),
        savedAmountPerItem: numberWrapper(
          formatter.format(ogBasePrice - perUnitNewPrice)
        ),
        savedPercentage:
          offer.discountType === "percentage"
            ? `${tier?.unitDiscount || 0}%`
            : offer.discountApplicationMethod === "perOrder"
              ? `${Math.round((unitDiscount / (ogBasePrice * button.quantity)) * 100)}%`
              : `${Math.round(((ogBasePrice - perUnitNewPrice) / ogBasePrice) * 100)}%`,
        totalComparePrice: !focusedOfferVariant?.compareAtPrice
          ? undefined
          : numberWrapper(
              formatter.format(
                focusedOfferVariant
                  ? ensureNumber(focusedOfferVariant.compareAtPrice ?? 0) *
                      button.quantity
                  : 0
              )
            ),
      };

      return Promise.resolve(state);
    },
    [offer, formatter, focusedOfferVariant]
  );

  return (
    <Grid item sx={{ ...gridSx }}>
      <QuantityButtonsView
        renderType={device}
        config={config}
        quantity={quantity}
        formatter={formatter.format}
        calculateButtonState={calculateButtonState}
        onQuantityChange={setQuantity}
      />
    </Grid>
  );
};

function getTierForQuantity(offer: OfferDto, quantity: number | undefined) {
  if (quantity === undefined) {
    return undefined;
  }
  return offer.tiers.findLast((t) => {
    return quantity >= t.minimumUnits;
  });
}
